import { z } from "zod";
import { utcDatetime } from "../../../helpers/date";
import { AgentAccessGrantTargetSchema } from "../agentAccess/agent-access-grant.schema";
import { PatPermissionSchema } from "../personalAccessToken/personal-access-token.schema";

export const CliSessionStatusSchema = z.enum([
    "pending",
    "approved",
    "consumed",
    "expired",
    "cancelled",
]);
export type CliSessionStatus = z.infer<typeof CliSessionStatusSchema>;

export const CreateCliSessionSchema = z.object({
    /**
     * sha256(deviceCode) computed by the CLI. The plaintext device code stays
     * on the CLI until it hits the `claim` endpoint.
     */
    deviceCodeHash: z.string().regex(/^[a-f0-9]{64}$/),
    hostname: z.string().trim().min(1).max(255),
    /**
     * Fields the CLI would like to pre-populate on the consent page. The SPA
     * honours them only when the user hasn't edited the corresponding form
     * field.
     */
    requestedScope: PatPermissionSchema.nullable().optional(),
    requestedWorkspaceId: z.string().uuid().nullable().optional(),
    requestedExpiresInDays: z
        .number()
        .int()
        .min(1)
        .max(365)
        .nullable()
        .optional(),
    requestedName: z.string().trim().min(1).max(120).nullable().optional(),
    noPrompt: z.boolean().optional(),
});
export type CreateCliSessionInput = z.infer<typeof CreateCliSessionSchema>;

/**
 * Polling interval defaults to `5` seconds; the CLI is free to back off if it
 * sees a `slow_down`-style signal in a future revision.
 */
export const CreateCliSessionResponseSchema = z.object({
    sessionId: z.string().uuid(),
    fingerprint: z.string(),
    authorizeUrl: z.string().url(),
    pollIntervalSec: z.number().int().positive(),
    expiresAt: utcDatetime,
});

export const CliSessionSchema = z.object({
    id: z.string().uuid(),
    fingerprint: z.string(),
    hostname: z.string(),
    status: CliSessionStatusSchema,
    requestedScope: PatPermissionSchema.nullable(),
    requestedWorkspaceId: z.string().uuid().nullable(),
    requestedExpiresInDays: z.number().int().nullable(),
    requestedName: z.string().nullable(),
    noPrompt: z.boolean(),
    expiresAt: utcDatetime,
    createdAt: utcDatetime,
});
export type CliSession = z.infer<typeof CliSessionSchema>;

export const ApproveCliSessionSchema = z.object({
    permissions: z.array(PatPermissionSchema).min(1),
    grantTarget: AgentAccessGrantTargetSchema.optional(),
    workspaceId: z.string().uuid().nullable().optional(),
    /** `null` disables expiration; else a positive day count. */
    expiresInDays: z.number().int().min(1).max(365).nullable(),
    name: z.string().trim().min(1).max(120).optional(),
});
export type ApproveCliSessionInput = z.infer<typeof ApproveCliSessionSchema>;

export const ApproveCliSessionResponseSchema = z.object({
    status: CliSessionStatusSchema,
});

export const ClaimCliSessionSchema = z.object({
    deviceCode: z.string().min(16).max(256),
});

/**
 * `plaintext` and `tokenId` are only present on the one successful claim. A
 * later `consumed` response omits them so the CLI can tell the user to start a
 * new login session.
 */
export const ClaimCliSessionResponseSchema = z.object({
    status: CliSessionStatusSchema,
    plaintext: z.string().optional(),
    tokenId: z.string().uuid().optional(),
    expiresAt: utcDatetime.nullable().optional(),
});

export const CliSessionIdPathParamsSchema = z.object({
    id: z.string().uuid(),
});
